import { PlusOutlined } from "@ant-design/icons";
import { Form } from "antd";
import { Collapse, Input } from "antd-v5";
import Typography from "antd-v5/lib/typography/Typography";
import AuthorisedContainer from "components/AuthorisedContainer";
import message from "components/CustomMessage";
import CustomSelect from "components/CustomSelect";
import DeletePopup from "components/DeletePopup";
import { DeleteIcon, EditIcon } from "components/Icons";
import { getVocabulary } from "components/Vocabulary";
import { PermissionSetMap } from "constants/RBACConstants";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import useModalRenderer from "hooks/useModalRenderer";
import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import ReactDragListView from "react-drag-listview";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { TierDto, TierTagDto } from "types/api/tierBasedTagsDto";
import ModalUtil from "utils/modalUtil";
import { TagObj, getTagKey, getTagKeyMap } from "utils/tierUtil";

import ACTable from "ac-components/ACTable/ACTable";
import ACButton from "ac-components/ac-button/ACButton";
import ACModalV2 from "ac-components/ac-modal/ACModalV2";
import ACTagColored from "ac-components/ac-tag-colored/ACTagColored";

// Extended TagObj to include optional properties needed for our component
interface ExtendedTagObj extends TagObj {
  id?: string;
  value?: number;
}

interface TierBasedTagsProps {
  allTags?: ExtendedTagObj[];
  onDirtyChange?: (isDirty: boolean) => void;
}

interface AddTagsToTierPopupProps {
  tier: TierDto;
  allTags: ExtendedTagObj[];
  saveTags: (selectedTags: ExtendedTagObj[]) => Promise<void>;
  afterClose?: () => void;
}

interface EditTierNamePopupProps {
  tier: TierDto;
  onSave: (tierId: string | number, newName: string) => Promise<void>;
  afterClose?: () => void;
}

const TierBasedTagsV2 = forwardRef<unknown, TierBasedTagsProps>(
  function TierBasedTags({ allTags = [], onDirtyChange }, ref) {
    const [tiers, setTiers] = useState<TierDto[]>([]);
    const [activeKeys, setActiveKeys] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const { context: modalContext, show: showModal } = useModalRenderer();
    const globalSettings = useGlobalSettingsContext();
    const { tierBasedTags, updateTierBasedTagsSettings } = globalSettings;

    useEffect(() => {
      if (tierBasedTags) {
        setTiers(tierBasedTags);
        setActiveKeys([`collapse-${tierBasedTags[0]?.id}`]);
      }
    }, [tierBasedTags]);

    useEffect(() => {
      if (!onDirtyChange || !tierBasedTags) return;
      onDirtyChange(!_.isEqual(tiers, tierBasedTags));
    }, [tiers, tierBasedTags, onDirtyChange]);

    useImperativeHandle(
      ref,
      () => ({
        syncTags: async (_tags: ExtendedTagObj[]) => {
          const allTagsMap = getTagKeyMap(_tags || allTags);
          const tierData = _.map(tiers, (tier) => {
            const currentTier = _.find(tierBasedTags, { name: tier.name });
            const existingTagKeys = _.map(currentTier?.tags, getTagKey);
            const updatedTags = (currentTier?.tags || []).filter(
              (tagObj) => allTagsMap[getTagKey(tagObj)],
            );
            const updatedTagKeys = _.map(updatedTags, getTagKey);
            const removedTags = _.difference(existingTagKeys, updatedTagKeys);

            const newTier = { ...tier };
            newTier.tags = _.filter(
              _.map(newTier.tags, (tagObj) => ({
                ...tagObj,
                ...allTagsMap[getTagKey(tagObj)],
              })),
              (tag) => !removedTags.includes(getTagKey(tag)),
            );

            return newTier;
          });
          // Update tiers data with new tags from backend
          const { promise } = GlobalSettingAPI.updateTierBasedTagsV2(tierData);
          promise.then((response) =>
            updateTierBasedTagsSettings(JSON.parse(response.config)),
          );
          setTiers(tierData);
          return promise;
        },
      }),
      [allTags, tierBasedTags, tiers, updateTierBasedTagsSettings],
    );

    // Drag and drop handler
    const onDragEnd = useCallback(
      (fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex) {
          return;
        }

        const newTiers = Array.from(tiers);
        const [reorderedItem] = newTiers.splice(fromIndex, 1);
        newTiers.splice(toIndex, 0, reorderedItem);

        setTiers(newTiers);
      },
      [tiers],
    );

    // Add a new tier
    const addTier = useCallback(() => {
      const newTier: TierDto = {
        id: `temp-${Date.now()}`, // Temporary ID, backend will generate real one
        name: `Tier ${tiers.length + 1}`,
        tags: [],
      };
      setTiers((prev) => [...prev, newTier]);
      setActiveKeys((prev) => [...prev, `collapse-${newTier.id}`]);
    }, [tiers.length]);

    //Delete tier
    const onDeleteTier = useCallback(async (tierId: string | number) => {
      const response =
        await GlobalSettingAPI.validateTierBasedTags(tierId).promise;
      let promptMessage = "";
      if (response.data?.productCount || response.data?.subProductCount) {
        promptMessage = `This tier is in use. Deleting it will unassign it from all linked ${getVocabulary("product", true)}/${getVocabulary("subProduct", true)}. Do you want to proceed?`;
      }
      return ModalUtil.show({
        content: (
          <DeletePopup
            // @ts-expect-error DeletePopup is a jsx file
            note={false}
            promptMessage={promptMessage}
            confirmText={"Delete"}
            onDelete={async () => {
              setTiers((prev) => prev.filter((tier) => tier.id !== tierId));
              setActiveKeys((prev) =>
                prev.filter((key) => key !== `collapse-${tierId}`),
              );
            }}
          />
        ),
      });
    }, []);

    // Handle tag deletion
    const handleTagDelete = useCallback(
      (tierId: string | number, record?: TierTagDto) => {
        setTiers((prev) =>
          prev.map((tier) =>
            tier.id === tierId
              ? {
                  ...tier,
                  // Incase of old tiers, id for tags may not be present, check name + fieldValue in such cases
                  tags: tier.tags.filter((tag) =>
                    record?.id
                      ? tag.id !== record.id
                      : `${tag.name}:${tag.fieldValue}` !==
                        `${record?.name}:${record?.fieldValue}`,
                  ),
                }
              : tier,
          ),
        );
      },
      [],
    );

    // Handle adding tags to a tier
    const saveTags = useCallback(
      async (
        tierId: string | number,
        selectedTags: ExtendedTagObj[],
      ): Promise<void> => {
        const _selectedTags = _.compact(selectedTags);

        setTiers((prev) =>
          prev.map((tier) =>
            tier.id === tierId
              ? {
                  ...tier,
                  tags: _selectedTags.map(
                    (tag): TierTagDto => ({
                      id:
                        tag.id && !tag.id.startsWith("temp-")
                          ? tag.id
                          : undefined, // Filter out temp IDs
                      name: tag.name,
                      fieldValue: tag.fieldValue ?? "",
                      value: tag.value ?? 1,
                    }),
                  ),
                }
              : tier,
          ),
        );
      },
      [],
    );

    // Add tags click handler
    const addTagsClick = useCallback(
      (tier: TierDto) => {
        showModal({
          content: (
            <AddTagsToTierPopup
              tier={tier}
              allTags={allTags}
              saveTags={(selectedTags) => saveTags(tier.id, selectedTags)}
            />
          ),
        });
      },
      [allTags, saveTags, showModal],
    );

    // Edit tier name handler
    const editTierName = useCallback(
      (tierId: string | number) => {
        const tier = tiers.find((t) => t.id === tierId);
        if (!tier) return;

        showModal({
          content: (
            <EditTierNamePopup
              tier={tier}
              onSave={async (id: string | number, newName: string) => {
                setTiers((prev) =>
                  prev.map((t) => (t.id === id ? { ...t, name: newName } : t)),
                );
              }}
            />
          ),
        });
      },
      [tiers, showModal],
    );

    // Calculate total weighting for a tier
    const getTotalWeighting = useCallback((tags: TierTagDto[]) => {
      return tags.reduce((acc, tag) => acc + (tag.value || 0), 0);
    }, []);

    // Save tiers to backend
    const saveTiers = useCallback(async () => {
      setSaving(true);
      try {
        const response = await GlobalSettingAPI.updateTierBasedTagsV2(
          tiers.map((tier) => ({
            ...tier,
            id:
              tier.id && !tier.id.toString().startsWith("temp-")
                ? tier.id
                : undefined, // Filter out temp IDs
          })),
        ).promise;
        //sending parsed config response for updating tiers in global settings
        //as the payload does not have the id for the newly added tier
        //it is returned in the response
        await updateTierBasedTagsSettings(JSON.parse(response.config));
        message.success("Tiers saved successfully!");
      } catch (error) {
        console.error("Error saving tiers:", error);
        message.error("Failed to save tiers. Please try again.");
      } finally {
        setSaving(false);
      }
    }, [updateTierBasedTagsSettings, tiers]);

    // Generate table columns for tags
    const getTagColumns = useCallback(
      (tierId: string | number) => [
        {
          title: "Tag Name",
          dataIndex: "name",
          key: "name",
          width: "30%",
          render: (name: string) => <b>{name}</b>,
        },
        {
          title: "Value",
          dataIndex: "fieldValue",
          key: "fieldValue",
          width: "25%",
        },
        {
          title: "Weighting",
          dataIndex: "value",
          key: "value",
          width: "20%",
        },
        {
          title: "Action",
          key: "actions",
          width: "25%",
          render: (_: unknown, record: TierTagDto) => (
            <AuthorisedContainer
              permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}
            >
              <ACButton
                type="ghost"
                onClick={() => handleTagDelete(tierId, record)}
                size="sm"
                title="Delete Tag"
              >
                <DeleteIcon />
              </ACButton>
            </AuthorisedContainer>
          ),
        },
      ],
      [handleTagDelete],
    );

    // Generate collapse items
    const collapseItems = useMemo(() => {
      return tiers.map((tier) => ({
        key: `collapse-${tier.id}`,
        label: (
          <div className="tier-header">
            <div className="d-flex justify-content-between">
              <span style={{ fontWeight: "bold" }}>{tier.name}</span>
              <div>
                <ACTagColored
                  dot={false}
                  color="Neutral"
                  label={`${getTotalWeighting(tier.tags)} Total Asset Weighting`}
                />
                <ACTagColored
                  dot={false}
                  color="Neutral"
                  label={`${tier.tags.length} Risk Tags`}
                />
              </div>
            </div>
          </div>
        ),
        children: (
          <div>
            <ACTable
              columns={getTagColumns(tier.id)}
              dataSource={tier.tags}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: "No tags in this tier",
              }}
            />
            <div style={{ marginTop: 16 }}>
              <AuthorisedContainer
                permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}
              >
                <ACButton
                  type="outline"
                  size="sm"
                  onClick={() => addTagsClick(tier)}
                  leftIcon={<PlusOutlined />}
                >
                  Add Tags
                </ACButton>
              </AuthorisedContainer>
            </div>
          </div>
        ),
        extra: (
          <div onClick={(e) => e.stopPropagation()}>
            <AuthorisedContainer
              permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}
            >
              <ACButton
                type="ghost"
                onClick={() => editTierName(tier.id)}
                size="sm"
              >
                <EditIcon />
              </ACButton>
              <ACButton
                type="ghost"
                onClick={() => onDeleteTier(tier.id)}
                size="sm"
              >
                <DeleteIcon />
              </ACButton>
            </AuthorisedContainer>
          </div>
        ),
      }));
    }, [
      tiers,
      getTotalWeighting,
      getTagColumns,
      onDeleteTier,
      addTagsClick,
      editTierName,
    ]);

    return (
      <>
        <div className="w-100">
          <ReactDragListView
            onDragEnd={onDragEnd}
            nodeSelector=".tier-drag-item"
            handleSelector=".tier-drag-handle"
          >
            <div>
              {tiers.map((tier) => (
                <div
                  key={tier.id}
                  className="tier-drag-item"
                  style={{
                    marginBottom: 8,
                    position: "relative",
                  }}
                >
                  <div
                    className="tier-drag-handle"
                    style={{
                      position: "absolute",
                      left: -10,
                      top: 4,
                      cursor: "grab",
                      padding: 4,
                      borderRadius: 4,
                      fontSize: "20px",
                      color: "#666",
                      zIndex: 10,
                    }}
                  >
                    ⋮⋮
                  </div>
                  <Collapse
                    items={(() => {
                      const item = collapseItems.find(
                        (item) => item.key === `collapse-${tier.id}`,
                      );
                      return item ? [item] : [];
                    })()}
                    activeKey={
                      activeKeys.includes(`collapse-${tier.id}`)
                        ? [`collapse-${tier.id}`]
                        : []
                    }
                    onChange={(keys) => {
                      if (keys.includes(`collapse-${tier.id}`)) {
                        setActiveKeys((prev) => [
                          ...prev.filter((k) => k !== `collapse-${tier.id}`),
                          `collapse-${tier.id}`,
                        ]);
                      } else {
                        setActiveKeys((prev) =>
                          prev.filter((k) => k !== `collapse-${tier.id}`),
                        );
                      }
                    }}
                    size="small"
                    style={{
                      marginLeft: 10,
                      border: "1px solid #d9d9d9",
                    }}
                  />
                </div>
              ))}
            </div>
          </ReactDragListView>

          <div className="m-t-m d-flex justify-content-between">
            <div className="d-flex align-items-center">
              <AuthorisedContainer
                permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}
              >
                <ACButton
                  type="primary"
                  leftIcon={<PlusOutlined />}
                  onClick={addTier}
                  disabled={tiers.length > 9}
                >
                  Add Tier
                </ACButton>
              </AuthorisedContainer>
              <Typography className="m-l-m" style={{ color: "#999" }}>
                Maximum of 10 tiers allowed.
              </Typography>
            </div>
            <AuthorisedContainer
              permission={PermissionSetMap.UPDATE_GLOBAL_SETTING}
            >
              <ACButton
                type="primary"
                size="md"
                onClick={saveTiers}
                loading={saving}
              >
                Save Tiers
              </ACButton>
            </AuthorisedContainer>
          </div>
        </div>
        {modalContext}
      </>
    );
  },
);

// Add Tags Modal Component
const AddTagsToTierPopup = forwardRef<HTMLDivElement, AddTagsToTierPopupProps>(
  function AddTagsToTierPopup(props) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const modalSettings = ACModalV2.useModalSettings();
    const { tier, allTags, saveTags } = props;

    const handleOk = async () => {
      setLoading(true);
      try {
        const values = await form.validateFields();
        const allTagsMap = getTagKeyMap(allTags);
        const selectedTags = _.map(values.tags ?? [], (tagString: string) => {
          const baseTag = allTagsMap[tagString];
          return {
            id: (baseTag as ExtendedTagObj)?.id, // Keep existing ID or undefined for new tags
            name: baseTag?.name ?? "",
            fieldValue: baseTag?.fieldValue ?? "",
            value: (baseTag as ExtendedTagObj)?.value ?? 1,
          } as ExtendedTagObj;
        });
        await saveTags(selectedTags);
        modalSettings.close();
      } catch (error) {
        console.error("Form validation failed:", error);
      } finally {
        setLoading(false);
      }
    };

    const getOptions = useCallback(
      () =>
        _.map(allTags, (tagObj: ExtendedTagObj) => {
          const tagData = form.getFieldsValue().tags;
          const selectedTags =
            tagData ?? _.map(tier.tags, (i: TierTagDto) => getTagKey(i));
          const values = tagData
            ? _.map(form.getFieldsValue().tags, (i: string) => i.split(":")[0])
            : _.map(tier.tags, (i: TierTagDto) => i.name);
          const key = getTagKey(tagObj);
          return {
            label: `${key} (${(tagObj as ExtendedTagObj).value ?? 1})`,
            value: key,
            disabled:
              !selectedTags.includes(key) && values.includes(tagObj.name),
          };
        }),
      [allTags, form, tier.tags],
    );

    const selectedTagKeys = useMemo(
      () => _.map(tier.tags, getTagKey),
      [tier.tags],
    );

    const tagOptions = useMemo(() => getOptions(), [getOptions]);

    return (
      <>
        <ACModalV2.Title>Add Tags to {tier.name}</ACModalV2.Title>
        <div style={{ padding: "20px" }}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              tags: selectedTagKeys,
            }}
          >
            <Form.Item
              name="tags"
              label="Select tags to add to this tier"
              rules={[
                { required: true, message: "Please select at least one tag" },
              ]}
            >
              {/** @ts-expect-error: Custom select is not type safe */}
              <CustomSelect
                mode="multiple"
                placeholder="Search and select tags"
                options={tagOptions}
                style={{ width: "100%" }}
                showSearch
                optionsMutator={getOptions}
              />
            </Form.Item>
          </Form>
        </div>
        <ACModalV2.Footer>
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
          >
            <ACButton type="primary" onClick={handleOk} loading={loading}>
              Add Tags
            </ACButton>
          </div>
        </ACModalV2.Footer>
      </>
    );
  },
);

// Edit Tier Name Modal Component
const EditTierNamePopup = forwardRef<HTMLDivElement, EditTierNamePopupProps>(
  function EditTierNamePopup(props) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const modalSettings = ACModalV2.useModalSettings();
    const { tier, onSave } = props;

    const handleOk = async () => {
      setLoading(true);
      try {
        const values = await form.validateFields();
        await onSave(tier.id, (values.name as string).trim());
        modalSettings.close();
      } catch (error) {
        console.error("Form validation failed:", error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <>
        <ACModalV2.Title>Edit Tier Name</ACModalV2.Title>
        <div style={{ padding: "20px" }}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              name: tier.name,
            }}
          >
            <Form.Item
              name="name"
              label="Tier Name"
              rules={[
                { required: true, message: "Tier name is required" },
                { min: 1, message: "Tier name cannot be empty" },
                { max: 50, message: "Tier name cannot exceed 50 characters" },
              ]}
            >
              <Input placeholder="Enter tier name" maxLength={50} showCount />
            </Form.Item>
          </Form>
        </div>
        <ACModalV2.Footer>
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
          >
            <ACButton type="primary" onClick={handleOk} loading={loading}>
              Save
            </ACButton>
          </div>
        </ACModalV2.Footer>
      </>
    );
  },
);

export default TierBasedTagsV2;
export { AddTagsToTierPopup };
